const Axios = require('axios').default;
const https = require('https');
const httpsAgent = new https.Agent({ keepAlive: true });
const axios = Axios.create({
    timeout: 300000,
    httpsAgent
});
const stringUtils = require('./stringUtils');
let API_URL = process.env.CAREPOINT_URL || process.env.REACT_APP_CAREPOINT_URL || "https://provider-api.centivo.dev/";
const DEFAULT_NETWORK = 'n/a';
const DEFAULT_DISTANCE = 20;
//axios.defaults.timeout = 30000;
class ProviderClient {

    constructor(apiUrl = API_URL) {
        API_URL = apiUrl;
        this.DEFAULT_HEADERS = {
            'content-type': 'application/json'
        };
    }

    networkEncode(network) {
        if (network != null && !Array.isArray(network)) {
            if (typeof network === 'string') {
                network = network.toLowerCase();
            }
        }
        return network;
    }

    /**
     * @param {*} network 
     * @param {*} state 
     * @param {*} city 
     * @param {*} zipCode 
     * @param {*} address1 
     * @param {*} address2 
     * @param {*} practiceName 
     */
    generateDoctorKey(network, state, city, zipCode, address1, address2 = null, practiceName) {
        // remove all space from network
        // network = network.replace(/ /g, '');
        // TODO: remove, old key return `${network}#${state}#${city}#${zipCode}#${address1}#${address2}#${practiceName}`.toLowerCase();
        return stringUtils.sanityStringLowerCaseNoSpace(`${network}#${state}#${city}#${zipCode}#`
            + stringUtils.sanityStringNumbersAndLettersSquishedLowerCase(address1 + address2)
            + '#' + practiceName);
    }

    /**
     * @param {*} doctor 
     */
    generateDoctorKeyFromDoctor(doctor) {
        if (doctor == null) {
            return null;
        }
        return this.generateDoctorKey(doctor.network, doctor.state, doctor.city, doctor.zipCode, doctor.address1, doctor.address2, doctor.practiceName);
    }

    /**
     * @param {*} member 
     */
    generateDoctorKeyFromMember(member) {
        if (member == null) {
            return null;
        }
        return this.generateDoctorKey(member.pcpNetwork, member.pcpState, member.pcpCity,
            member.pcpZipCode, member.pcpAddress1, member.pcpAddress2, member.pcpPracticeName);
    }

    /**
     * @param {*} referral 
     * @returns {
     *  referredByKey --> should never be null
     *  referredToKey --> can be null (if specialty only referral)
     * }
     */
    generateDoctorKeyFromReferral(referral) {
        let referredByKey = null;
        let referredToKey = null;
        if (referral == null) {
            return null;
        }
        if (referral.referredByPcpNpi != null) {
            referredByKey = this.generateDoctorKey(referral.referredByPcpNetwork, referral.referredByPcpState, referral.referredByPcpCity,
                referral.referredByPcpZipCode, referral.referredByPcpAddress1,
                referral.referredByPcpAddress2 ? referral.referredByPcpAddress2 : null, referral.referredByPcpPracticeName);
        }
        if (referral.referredToPcpNpi != null) {
            referredToKey = this.generateDoctorKey(referral.referredToPcpNetwork, referral.referredToPcpState, referral.referredToPcpCity,
                referral.referredToPcpZipCode, referral.referredToPcpAddress1,
                referral.referredToPcpAddress2 ? referral.referredToPcpAddress2 : null, referral.referredToPcpPracticeName);
        }
        return {
            referredByKey,
            referredToKey
        }
    }

    /**
     * @param {*} npi 
     * @param {*} key 
     */
    async getRectifyCarepoint(npi, key) {
        const response = await axios.get(`${API_URL}getRectifyCarepoint`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                npi: npi,
                key: key
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} network 
     * @param {*} lat 
     * @param {*} long 
     * @param {*} isPcp 
     */
    async getCarepointFacilityByNetworkLatAndLong(network, lat, long, practiceName, isPcp = true, age = undefined, specialties = undefined, publishOnly = undefined) {
        const response = await axios.get(`${API_URL}getCarepointFacilityByNetworkLatAndLong`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                network: this.networkEncode(network),
                lat: lat,
                long: long,
                practiceName: practiceName,
                isPcp: isPcp,
                age: age,
                specialties: specialties,
                publishOnly
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @endpoint [provider-domain]/getCarepointFacilityByNetworkLatAndLong
     * 
     * @param {{
     *  network: string,
     *  lat: string,
     *  long: string,
     *  practiceName?: string,
     *  isPcp?: boolean,
     *  age?: number,
     *  specialties?: string,
     *  publishOnly?: boolean,
     *  virtualOptions?: "Y" | "N" | "only" | listWithCommas
     * }} reqParams
     * 
     * @return Promise<AxiosResponse | null>
     */
    async getCarepointFacilityByNetworkLatAndLongV2(reqParams) {
        const {
            network, 
            isPcp = true, 
        } = reqParams;

        const response = await axios.get(`${API_URL}getCarepointFacilityByNetworkLatAndLong`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                network: this.networkEncode(network),
                isPcp,
                ...reqParams
            }
        });

        if (response.status !== 200) return null;
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     * @param {*} gender 
     * @param {*} isPcp 
     * @param {*} age 
     * @param {*} specialties 
     * @param {*} isAcceptingNewPatients
     * @param {*} checkTooBig
     * @param {*} tooBigSizeMB
     */
    async getLocationCarepointProvidersByZipCode(zipCode, network = DEFAULT_NETWORK, distance = DEFAULT_DISTANCE, gender,
        isPcp = true, age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, publishOnly = undefined,
        checkTooBig = false, tooBigSizeMB = 5, headers = this.DEFAULT_HEADERS, limitStart = undefined, limitEnd = undefined) {
        const response = await axios.get(`${API_URL}getLocationCarepointProvidersByZipCode`, {
            headers: headers,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                distance: distance,
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                publishOnly: publishOnly,
                checkTooBig: checkTooBig,
                tooBigSizeMB: tooBigSizeMB,
                limitStart: limitStart,
                limitEnd: limitEnd,
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     * @param {*} gender 
     * @param {*} isPcp 
     * @param {*} age 
     * @param {*} specialties 
     * @param {*} isAcceptingNewPatients 
     * @param {*} publishOnly
     * @param {*} virtualOptions
     */
    async getCarepointFacilitiesByZipCodeAndNetwork(
        zipCode,
        network = DEFAULT_NETWORK,
        distance = DEFAULT_DISTANCE,
        gender,
        isPcp = true,
        age = undefined,
        specialties = undefined,
        isAcceptingNewPatients = undefined,
        publishOnly = undefined,
        virtualOptions = undefined
    ) {
        const response = await axios.get(`${API_URL}getCarepointFacilitiesByZipCode`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                distance: distance,
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                publishOnly,
                virtualOptions,
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @endpoint [provider-domain]/getCarepointFacilitiesByZipCode
     * 
     * @param {{
     *  zipCode: string
     *  network: string,
     *  distance: number,
     *  isPcp?: boolean,
     *  gender?: "m" | "f"
     *  age?: number
     *  specialties?: string
     *  isAcceptingNewPatients?: boolean
     *  publishOnly?: boolean
     *  virtualOptions?: "Y" | "N" | "only" | listWithCommas
     * }} reqParams 
     * 
     * @return {Promise<AxiosResponse | null>}
     */
    async getCarepointFacilitiesByZipCode(reqParams) {
        const {
            network = DEFAULT_NETWORK, 
            distance = DEFAULT_DISTANCE, 
            isPcp = true, 
        } = reqParams;

        const response = await axios.get(`${API_URL}getCarepointFacilitiesByZipCode`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                network: this.networkEncode(network),
                distance,
                isPcp,
                ...reqParams
            }
        });
        if (response.status !== 200) return null;
        return response.data;
    }

    /**
     * @param {*} network 
     * @param {*} gender 
     * @param {*} isPcp 
     * @param {*} age 
     * @param {*} specialties 
     * @param {*} isAcceptingNewPatients 
     * @param {*} publishOnly
     * @param {*} virtualOptions
     */
    async getCarepointFacilitiesByNetwork(network = DEFAULT_NETWORK, gender,
        isPcp = true, age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, virtualOptions = undefined) {
        const response = await axios.get(`${API_URL}getCarepointFacilitiesByZipCode`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                virtualOptions,
            }
        });

        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} address 
     * @param {*} network 
     * @param {*} distance 
     * @param {*} gender 
     * @param {*} isPcp 
     * @param {*} age 
     * @param {*} specialties 
     * @param {*} isAcceptingNewPatients 
     * @param {*} virtualOptions
     */
    async getCarepointFacilitiesByNetworkAndAddress(address, network = DEFAULT_NETWORK, distance = DEFAULT_DISTANCE, gender,
        isPcp = true, age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, virtualOptions = undefined) {
        const response = await axios.get(`${API_URL}getCarepointFacilitiesByNetworkAndAddress`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                address: address,
                network: this.networkEncode(network),
                distance: distance,
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                virtualOptions: virtualOptions,
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} virtualOption 
     * @param {*} gender 
     * @param {*} isPcp
     * @param {*} age
     * @param {*} specialties
     * @param {*} isAcceptingNewPatients
     * @param {*} lastEvaluatedKey 
     * @param {*} publishOnly
     */
    async getCarepointFacilitiesByStateAndNetwork(zipCode, network = DEFAULT_NETWORK, virtualOption, gender, isPcp = true,
        age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, lastEvaluatedKey = undefined, publishOnly = undefined) {
        const response = await axios.get(`${API_URL}getCarepointFacilitiesByStateAndNetwork`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                virtualOption: virtualOption,
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                lastEvaluatedKey: lastEvaluatedKey,
                publishOnly
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} virtualOption 
     * @param {*} gender 
     * @param {*} isPcp
     * @param {*} age
     * @param {*} specialties
     * @param {*} isAcceptingNewPatients
     * @param {*} lastEvaluatedKey 
     * @param {*} publishOnly
     */
    async getCarepointProvidersByStateAndNetwork(zipCode, network = DEFAULT_NETWORK, virtualOption, gender, isPcp = true,
        age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, lastEvaluatedKey = undefined, publishOnly = undefined) {
        const response = await axios.get(`${API_URL}getLocationCarepointProvidersByStateAndNetwork`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                virtualOption: virtualOption,
                isPcp: isPcp,
                gender: gender,
                age: age,
                specialties: specialties,
                isAcceptingNewPatients: isAcceptingNewPatients,
                lastEvaluatedKey: lastEvaluatedKey,
                publishOnly
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     * @param {*} isPcp 
     */
    async getDoctorsByNpiNetworkLatAndLong(npi, network, lat, long, practiceName = undefined) {
        const response = await axios.get(`${API_URL}getDoctorsByNpiNetworkLatAndLong`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                npi: npi,
                network: this.networkEncode(network),
                lat: lat,
                long: long,
                practiceName
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     * @param {*} isPcp 
     * 
     * @deprecated use getDoctorsByNetworkAndNameV2 instead
     */
    async getDoctorsByNetworkAndName(firstName, lastName, network, specialties = undefined) {
        const response = await axios.get(`${API_URL}getDoctorsByNetworkAndName`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                firstName: firstName,
                lastName: lastName,
                network: this.networkEncode(network),
                specialties
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @endpoint [provider-domain]/getDoctorsByNetworkAndName
     *  
     * @param {{
     *  network: string,
     *  firstName: string,
     *  lastName: string,
     *  filterTerminated?: boolean,
     *  specialties?: string,
     *  virtualOptions?: "Y" | "N" | "only" | listWithCommas
     * }} reqParams 
     * 
     * @return {Promise<AxiosResponse | null>}
     */
    async getDoctorsByNetworkAndNameV2(reqParams) {
        const { network } = reqParams;

        const response = await axios.get(`${API_URL}getDoctorsByNetworkAndName`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                network: this.networkEncode(network),
                ...reqParams
            }
        });
        if (response.status !== 200) return null;
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     */
    async getDoctorsByNetworkAndZipCode(zipCode, network, distance = DEFAULT_DISTANCE) {
        const response = await axios.get(`${API_URL}getDoctorsByZipCode`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode: zipCode,
                network: this.networkEncode(network),
                distance: distance,
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} zipCode 
     * @param {*} network 
     * @param {*} distance 
     */
    async getDoctorsByNetworkZipCodeAndSpecialty(zipCode, network, specialty, distance = DEFAULT_DISTANCE) {
        let redirectUrl = '';
        try {
            const response = await axios.get(`${API_URL}getDoctorsByNetworkZipCodeAndSpecialty`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    zipCode: zipCode,
                    network: this.networkEncode(network),
                    specialty: specialty,
                    distance: distance
                },
                beforeRedirect: (options, { headers }) => {
                    if (options && options.href) {
                        redirectUrl = options.href;
                        throw new Error('redirect');
                    }
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        }
        catch (e) {
            if (redirectUrl) {
                const response = await axios.get(redirectUrl);
                if (response.status !== 200) {
                    return null;
                }
                return response.data;
            } else {
                throw e;
            }
        }
    }

    async getDoctorsByNetworkZipCodeAndSpecialties(zipCode, network, specialties, distance = DEFAULT_DISTANCE) {
        let redirectUrl = '';
        try {
            const response = await axios.get(`${API_URL}getDoctorsByNetworkZipCodeAndSpecialties`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    zipCode: zipCode,
                    network: this.networkEncode(network),
                    specialties: specialties,
                    distance: distance
                },
                beforeRedirect: (options, { headers }) => {
                    if (options && options.href) {
                        redirectUrl = options.href;
                        throw new Error('redirect');
                    }
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            if (redirectUrl) {
                const response = await axios.get(redirectUrl);
                if (response.status !== 200) {
                    return null;
                }
                return response.data;
            } else {
                throw e;
            }
        }
    }

    /**
     * @param {*} npi
     * @param {*} key
     * 
     * @throws axios error
     */
    async getDoctor(npi, key) {
        let response = null;
        try {
            response = await axios.get(`${API_URL}getDoctor`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    npi: npi,
                    key: key
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            throw e;
        }
    }

    /**
     * @param {*} npi
     * @param {*} key
     */
    async getDoctorShallow(npi, key) {
        try {
            const response = await axios.get(`${API_URL}getDoctorShallow`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    npi: npi,
                    key: key
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            // console.log(e);
            console.log("GET SHALLOW DOCTOR API ERROR:", npi, key);
            return null;
        }
    }

    /**
     * @param {*} npi
     * @param {*} key
     */
    async getDoctorWithShallowFallback(npi, key) {
        try {
            const response = await axios.get(`${API_URL}getDoctor`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    npi: npi,
                    key: key
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            // console.log(e);
            console.log("GET DOCTOR API ERROR:", npi, key);
            // TODO: this would be uncessary withdata 
            // redact key practicename
            key = key.substr(0, key.lastIndexOf("#"));
            return await this.getDoctorShallow(npi, key);
        }
    }

    /**
     * @param {*} npi
     */
    async getDoctorsByNpi(npi) {
        const response = await axios.get(`${API_URL}getDoctorsByNpi`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                npi: npi
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {{
    *   npi: string,
    *   networks: string[]
    * }} reqParams 
    * 
    * @return {Promise<AxiosResponse | null>}
    */

    async getDoctorsByNpiAndNetworks(reqParams) {
        const response = await axios.get(`${API_URL}getDoctorsByNpiAndNetworks`, {
            headers: this.DEFAULT_HEADERS,
            params: reqParams
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    async getDoctorsByTin(tin, lastEvaluatedKey = null) {
        const response = await axios.get(`${API_URL}getDoctorsByTin`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                tin,
                lastEvaluatedKey
            }
        });
        if (response.status !== 200) {
            return null;
        }
        // contains items and lasteval
        return response.data;
    }

    /**
     * @param {*} firstName 
     * @param {*} lastName 
     */
    async getDoctorsByName(firstName, lastName) {
        const response = await axios.get(`${API_URL}getDoctorsByName`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                firstName: firstName,
                lastName: lastName
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} tin 
     */
    async getMedicalNeighborhoodByTin(tin) {
        const response = await axios.get(`${API_URL}getMedicalNeighborhoodsByTin`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                tin: tin
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {*} tins (comma delimited tins)
     */
    async getMedicalNeighborhoodByTins(tins) {
        if (tins == null) {
            return [];
        }

        let tasks = [];
        for (let tin of tins.split(",")) {
            tasks.push(this.getMedicalNeighborhoodByTin(tin));
        }
        let medicalNeighborhoods = await Promise.all(tasks);
        let flatten = [];
        for (let item of medicalNeighborhoods) {
            flatten.push(...item);
        }
        return flatten;
    }

    /**
     * @param {{
     *  memberUuid?: string,
     *  isProvider?: boolean
     *  isMember?: boolean
     *  isCommonSpecialty?: boolean
     *  isFacility?: boolean
     *  isOe?: boolean
     * }} reqParams 
     * @return {Promise<AxiosResponse | null>}
     */
    async getSpecialties(reqParams) {
        const response = await axios.get(`${API_URL}getSpecialties`, {
            headers: this.DEFAULT_HEADERS,
            params: reqParams
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {{
     *  isOeDoctor: boolean
     *  isOeFacility: boolean
     * }} reqParams 
     */
    async getServiceLines(reqParams) {
        const response = await axios.get(`${API_URL}getServiceLines`, {
            headers: this.DEFAULT_HEADERS,
            params: reqParams
        });
        if (response.status !== 200) return null;
        return response.data;
    }

    /**
     * @param {*} network 
     * @param {*} state 
     * @param {*} city 
     * @param {*} zipCode 
     * @param {*} address1 
     * @param {*} address2
     */
    generateFacilityKey(network, state, city, zipCode, address1, address2 = null, specialtyType) {
        // remove all space from network
        // network = network.replace(/ /g, '');
        // TODO: remove old, return `${network}#${state}#${city}#${zipCode}#${address1}#${address2}#${specialtyType}`.toLowerCase();
        return utils.sanityStringLowerCaseNoSpace(`${network}#${state}#${city}#${zipCode}#`
            + utils.sanityStringNumbersAndLettersSquishedLowerCase(address1 + address2)
            + '#' + specialtyType);
    }

    /**
     * @param {*} facility 
     */
    generateFacilityKeyFromFacility(facility) {
        if (facility == null) {
            return null;
        }
        return this.generateFacilityKey(facility.network, facility.state, facility.city, facility.zipCode, facility.address1,
            facility.address2, facility.specialtyType);
    }

    async getFacility(tin, key) {
        try {
            const response = await axios.get(`${API_URL}getFacility`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    tin: tin,
                    key: key
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            // console.log(e);
            console.log("GET FACILITY API ERROR:", tin, key);
            return null;
        }
    }

    /**
     * @param {*} tin
     * @param {*} key
     */
    async getFacilitiesByTin(tin, key) {
        try {
            const response = await axios.get(`${API_URL}getFacilitiesByTin`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    tin: tin
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            // console.log(e);
            console.log("GET FACILITY API ERROR:", tin, key);
            return null;
        }
    }

    /**
     * @param {*} specialtyType 
     */
    async getFacilityBySpecialtyType(specialtyType) {
        try {
            const response = await axios.get(`${API_URL}getFacilityBySpecialtyType`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    specialtyType: specialtyType
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    /**
     * @param {*} zipCode 
     * @param {*} radius 
     * @param {*} networks 
     */
    async getFacilityByZip(zipCode, radius = 20, networks = undefined, limitStart = undefined, limitEnd = undefined) {
        try {
            const response = await axios.get(`${API_URL}getFacilityByZip`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    zipCode: zipCode,
                    radius: radius,
                    networks: this.networkEncode(networks),
                    limitStart: limitStart,
                    limitEnd: limitEnd,
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    /**
     * @param {*} specialtyType 
     * @param {*} zipCode 
     * @param {*} radius 
     * @param {*} networks filter option
     */
    async getFacilityBySpecialtyTypeAndZip(specialtyType, zipCode, radius = 20, networks = undefined) {
        try {
            const response = await axios.get(`${API_URL}getFacilityBySpecialtyTypeAndZip`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    specialtyType: specialtyType,
                    zipCode: zipCode,
                    radius: radius,
                    networks: this.networkEncode(networks)
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    /**
     * @param {*} specialtyCode 
     * @param {*} zipCode 
     * @param {*} radius 
     * @param {*} networks filter option
     */
    async getFacilityBySpecialtyCodeAndZip(specialtyCode, zipCode, radius = 20, networks = undefined) {
        try {
            const response = await axios.get(`${API_URL}getFacilityBySpecialtyCodeAndZip`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    specialtyCode: specialtyCode,
                    zipCode: zipCode,
                    radius: radius,
                    networks: this.networkEncode(networks)
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    /**
     * @param {*} name 
     * @param {*} zipCode 
     * @param {*} radius 
     * @param {*} networks filter option
     */
    async getFacilityByNameAndZip(name, zipCode, radius = 20, networks = undefined) {
        try {
            const response = await axios.get(`${API_URL}getFacilityByNameAndZip`, {
                headers: this.DEFAULT_HEADERS,
                params: {
                    name: name,
                    zipCode: zipCode,
                    radius: radius,
                    networks: this.networkEncode(networks)
                }
            });
            if (response.status !== 200) {
                return null;
            }
            return response.data;
        } catch (e) {
            console.log(e);
            return null;
        }
    }

    async getFacilityMap() {
        const response = await axios.get(`${API_URL}getFacilityMap`, {
            headers: this.DEFAULT_HEADERS,
            params: {
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    async getZipCodeLatAndLong(zipCode) {
        const response = await axios.get(`${API_URL}getZipCodeLatAndLong`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                zipCode
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    async getAllNetworks() {
        const response = await axios.get(`${API_URL}getAllNetworks`, {
            headers: this.DEFAULT_HEADERS,
            params: {
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    async getCarepointsByNetworkStateAndCity(network, state, city, filters, lastEvaluatedKey) {
        return await this.getResultsByNetworkStateAndCity('getDoctorsByNetworkStateAndCity',
            network, state, city, filters, lastEvaluatedKey);
    }

    async getFacilitiesByNetworkStateAndCity(network, state, city, filters, lastEvaluatedKey) {
        return await this.getResultsByNetworkStateAndCity('getFacilitiesByNetworkStateAndCity',
            network, state, city, filters, lastEvaluatedKey);
    }

    async getResultsByNetworkStateAndCity(urlExtension, network, state, city, filters, lastEvaluatedKey) {
        const response = await axios.get(`${API_URL}${urlExtension}`, {
            headers: this.DEFAULT_HEADERS,
            params: {
                network,
                state,
                city,
                filters,
                lastEvaluatedKey
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    // AGGREGATOR METHODS FOR NETWORK AGGREGATION CLIENT WORK //

    flattenResultSet(results) {
        const flat = [];
        for (let item of results) {
            flat.push(...item);
        }
        return flat;
    }

    /**
     * @param {*} firstName 
     * @param {*} lastName 
     * @param {*} networks 
     * @param {*} specialties 
     * 
     * @deprecated use getDoctorsByNetworkArrayAndName instead
     */
    async getDoctorsByNetworksAndName(firstName, lastName, networks = [DEFAULT_NETWORK], specialties = undefined) {
        const tasks = [];
        for (let network of networks) {
            tasks.push(this.getDoctorsByNetworkAndName(firstName, lastName, this.networkEncode(network), specialties));
        }
        const results = await Promise.all(tasks);
        return this.flattenResultSet(results);
    }

    /**
     * @endpoint multiple requests for [provider-domain]/getDoctorsByNetworkAndName
     *  
     * @param {{
     *  networks: string[],
     *  firstName: string,
     *  lastName: string,
     *  filterTerminated?: boolean,
     *  specialties?: string,
     *  virtualOptions?: "Y" | "N" | "only" | listWithCommas
     * }} reqParams 
     * 
     * @return {Promise<AxiosResponse | null>}
     */
     async getDoctorsByNetworkArrayAndName(reqParams) {
        const { networks = [DEFAULT_NETWORK] } = reqParams;
        const tasks = [];
        for (let network of networks) {
            tasks.push(this.getDoctorsByNetworkAndNameV2({
                network: this.networkEncode(network), 
                ...reqParams
            }));
        }
        const results = await Promise.all(tasks);
        return this.flattenResultSet(results);
    }

    /**
     * @param {*} zipCode 
     * @param {*} networks 
     * @param {*} distance 
     * @param {*} gender 
     * @param {*} isPcp 
     * @param {*} age 
     * @param {*} specialties 
     * @param {*} isAcceptingNewPatients 
     * @param {*} publishOnly
     * @param {*} virtualOptions  
     */
    async getCarepointFacilitiesByZipCodeAndNetworks(zipCode, networks = [DEFAULT_NETWORK], distance = DEFAULT_DISTANCE, gender,
        isPcp = true, age = undefined, specialties = undefined, isAcceptingNewPatients = undefined, publishOnly = undefined, virtualOptions = undefined) {
        const tasks = [];
        for (let network of networks) {
            tasks.push(this.getCarepointFacilitiesByZipCodeAndNetwork(zipCode, this.networkEncode(network),
                distance, gender, isPcp, age, specialties, isAcceptingNewPatients, publishOnly, virtualOptions));
        }
        const results = await Promise.all(tasks);
        return this.flattenResultSet(results);
    }

    /**
     * @param {*} specialtyType 
     * @param {*} zipCode 
     * @param {*} radius 
     * @param {*} networks
     */
    async getFacilityBySpecialtyTypeAndZipAggregate(specialtyType, zipCode, radius = 20, networks = undefined) {
        if (specialtyType.length == 2) {
            const facilityMap = await this.getFacilityMap();
            let results = facilityMap.filter(item => item.code.toString().toLowerCase() === specialtyType.toString().toLowerCase());
            let tasks = [];
            for (let item of results) {
                tasks.push(this.getFacilityBySpecialtyTypeAndZip(item.facilityType, zipCode, radius, networks));
            }
            results = await Promise.all(tasks);

            let flat = [];
            results.forEach(item => {
                if (item == null) {
                    return;
                }
                flat.push(...item);
            });
            if (flat.length < 1) {
                return [];
            }
            return flat;
        }

        const data = await this.getFacilityBySpecialtyTypeAndZip(specialtyType, zipCode, radius, networks);
        return data;
    }

    async getAllDoctorsByTin(tin) {
        if (tin == null) {
            return null;
        }
        tin = tin.toString();
        let resultSet = [];
        let tinResponseResult = await this.getDoctorsByTin(tin);
        if (tinResponseResult.items) {
            resultSet.push(...tinResponseResult.items);
        }
        while (tinResponseResult.LastEvaluatedKey) {
            tinResponseResult = await this.getDoctorsByTin(tin, tinResponseResult.LastEvaluatedKey);
            if (tinResponseResult.items) {
                resultSet.push(...tinResponseResult.items);
            }
        }
        return resultSet;
    }

    // UTILITY METHODS //

    /**
     * @param {*} specialtyList 
     * @param {*} specialty 
     */
    getSpecialtyDisplay(specialtyList, specialty) {
        if (stringUtils.isStringNumber(specialty) || specialty.toString().length === 2) {
            let result = specialtyList.filter((item) => item.code.toString() === specialty.toString());
            if (result.length > 0) {
                return result[0].specialty;
            }
        }
        return null;
    };

    /**
     * @param {*} doctor 
     * @param {*} virtualOnlyOptions
     */
    stripVirtualCareInformation(doctor, virtualOnlyOptions = ['only']) {
        // Deferred from using the most recent syntax on this because of backwards compatibility with older versions of ES 
        if (doctor == null || !doctor.virtualOption || !virtualOnlyOptions.includes(doctor.virtualOption.toLowerCase())) {
            return doctor;
        }
        doctor.address1 = "";
        doctor.address2 = "";
        doctor.zipCode = "";
        doctor.state = "";
        doctor.county = "";
        doctor.city = "";
        return doctor;
    }

    
  async getDoctorsByServiceLines(params) {
    try {
      const response = await axios.get(`${API_URL}getDoctorsByServiceLines`, { params });

      if (response.status !== 200) return null;
      return response.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async getFacilitiesByServiceLines(params) {
    try {
      const response = await axios.get(`${API_URL}getFacilitiesByServiceLines`, { params });

      if (response.status !== 200) return null;
      return response.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async getPractitionersByServiceLines(params) {
    try {
      const response = await axios.get(`${API_URL}getPractitionersByServiceLines`, { params });

      if (response.status !== 200) return null;
      return response.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }
}

module.exports = {
    ProviderClient,
    providerClient: new ProviderClient()
}